// "Off the record" (RECALL-PEOPLE-SURFACES; design §5.1): a toggle above the composer. From turning
// it on until turning it off, nothing said in this conversation is kept in memory. Per person and
// per conversation, works without the memory service, shown only with the `memory` add-on.
import { cn, InfoTip, Switch } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOffIcon } from "lucide-react";
import { useId } from "react";
import type { Api } from "../../api/client";
import { useFeature } from "../use-feature";

const key = (co: string) => ["company", co, "memory", "off-record"] as const;

export function OffTheRecord({
  api,
  co,
  channelId,
  disabled = false,
}: {
  api: Api;
  co: string;
  channelId: string;
  disabled?: boolean;
}) {
  const id = useId();
  const memory = useFeature("memory", co);
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: key(co),
    queryFn: () => api.client.memoryOffRecord.get({ params: { co } }),
    enabled: memory.enabled,
    retry: false,
    staleTime: 60_000,
  });
  const set = useMutation({
    mutationFn: (on: boolean) =>
      api.client.memoryOffRecord.set({ params: { co }, body: { channel_id: channelId, on } }),
    onSuccess: (r) => qc.setQueryData(key(co), r),
  });
  if (!memory.enabled || memory.readOnly || list.isError) return null;
  const on = list.data?.items.some((i) => i.channel_id === channelId) === true;
  return (
    <div
      className={cn(
        "flex items-center gap-2 self-start rounded-md px-1 text-[12px]",
        on ? "text-foreground" : "text-muted-foreground",
      )}
      data-slot="off-the-record"
      data-on={on}
    >
      <EyeOffIcon aria-hidden="true" className="size-3.5" />
      <label htmlFor={id}>Off the record</label>
      <Switch
        id={id}
        checked={on}
        disabled={disabled || list.isPending || set.isPending}
        onCheckedChange={(next) => set.mutate(next)}
      />
      <InfoTip label="About Off the record">
        Until you turn it off, nothing said in this chat is kept in memory.
      </InfoTip>
      {set.isError ? (
        <span role="alert" className="text-destructive">
          Could not change it.
        </span>
      ) : null}
    </div>
  );
}
